import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedProductsSection extends Component {
  requiredRefs = ['mount'];

  /** @type {AbortController | null} */
  #fetchAbort = null;

  #onStorage = (/** @type {StorageEvent} */ e) => this.#syncFromStorage(e);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('storage', this.#onStorage);
    this.#syncFromStorage();
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this.#onStorage);
    this.#fetchAbort?.abort();
    super.disconnectedCallback();
  }

  /** @param {StorageEvent} [event] */
  #syncFromStorage(event) {
    if (event && event.key !== null && event.key !== RecentlyViewed.storageKey) return;
    void this.#loadFromStorage();
  }

  async #loadFromStorage() {
    if (window.Shopify?.designMode) return;

    const mount = this.refs.mount instanceof HTMLElement ? this.refs.mount : null;
    const sectionId = this.dataset.sectionId;
    if (!mount || !sectionId || typeof Theme === 'undefined' || !Theme.routes?.search_url) return;

    const maxProducts = Number(this.dataset.maxProducts) || 8;
    const hideWhenEmpty = this.dataset.emptyStateBehavior !== 'collection';

    const ids = RecentlyViewed.getProducts().slice(0, maxProducts);

    if (ids.length === 0) {
      this.#fetchAbort?.abort();
      if (hideWhenEmpty) {
        this.hidden = true;
        mount.hidden = true;
        mount.innerHTML = '';
      } else {
        this.hidden = false;
        mount.hidden = false;
      }
      return;
    }

    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();

    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (this.#fetchAbort.signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = doc.getElementById(`shopify-section-${sectionId}`);
      const nextMount = sectionEl?.querySelector('[data-rvp-mount]');

      if (nextMount?.innerHTML?.trim()) {
        mount.innerHTML = nextMount.innerHTML;
        this.hidden = false;
        mount.hidden = false;
      } else if (hideWhenEmpty) {
        this.hidden = true;
        mount.hidden = true;
        mount.innerHTML = '';
      }
    } catch (e) {
      if (this.#fetchAbort.signal.aborted) return;
      if (!window.Shopify?.designMode) console.error('Recently viewed products:', e);
      if (hideWhenEmpty) {
        this.hidden = true;
        mount.hidden = true;
      }
    }
  }
}

if (!customElements.get('recently-viewed-products-section')) {
  customElements.define('recently-viewed-products-section', RecentlyViewedProductsSection);
}
